import { serviceInstance, wcfInstance } from '../../../axiosinstance';
import { getSession } from '../../../getSession';

const parseDetails = (response) => {
  if (!response) return null;

  // WCF usually wraps data in .d
  let data = response.d !== undefined ? response.d : response;

  // If data is already an array, return it
  if (Array.isArray(data)) return data;

  let details = data.Details || data;

  if (typeof details === 'string') {
    try {
      return JSON.parse(details);
    } catch (e) {
      return details;
    }
  }

  if (details && details.Text && typeof details.Text === 'string') {
    try {
      return JSON.parse(details.Text);
    } catch (e) {
      return details.Text;
    }
  }

  return details;
};

/**
 * Updates dialer data using the RequestParamEntity format.
 */
export const updateDialerData = async (mode, operationJson) => {
  const { userId, parentId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        UserId: userId,
        ParentId: parentId,
        Mode: mode,
        OperationJSON: typeof operationJson === 'string' ? operationJson : JSON.stringify(operationJson)
      })
    };
    const response = await serviceInstance.post('DialerSetting/UpdateDialerData', payload);
    return response.data;
  } catch (error) {
    console.error('updateDialerData error:', error);
    throw error;
  }
};

/**
 * Fetches scheduled calls for the dialer.
 */
export const getScheduleCallForDialer = async (recordCount = 10, scheduleTime = 0) => {
  const { userId, parentId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        UserId: userId,
        ParentId: parentId,
        RecordCount: recordCount,
        ScheduleTime: scheduleTime,
      })
    };
    const response = await serviceInstance.post('DialerSetting/GetScheduleCallForDailer', payload);
    return response.data;
  } catch (error) {
    console.error('getScheduleCallForDialer error:', error);
    throw error;
  }
};

/**
 * Fetches scheduled call data for the grid using DataTableDateRequest format.
 * Matches C# [Route("DialerSetting/GetScheduleCallDataForGrid")]
 */
export const getScheduleCallDataForGrid = async (params) => {
  const { userId, parentId, TokenId } = getSession();
  try {
    const payload = {
      ...params,
      UserId: userId,
      ParentId: parentId,
      Token: TokenId,
    };
    const response = await serviceInstance.post('DialerSetting/GetScheduleCallDataForGrid', payload);
    return response.data;
  } catch (error) {
    console.error('getScheduleCallDataForGrid error:', error);
    throw error;
  }
};

/**
 * Updates user page settings (e.g., GET_SET mode).
 */
export const updateUserPageSetting = async (id, setting) => {
  const { userId, parentId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        Mode: 'GET_SET',
        Id: id,
        SettingJSON: typeof setting === 'string' ? setting : JSON.stringify(setting),
        UserId: userId,
        ParentId: parentId
      })
    };
    const response = await serviceInstance.post('DialerSetting/GetScheduleCallAction', payload);
    return response.data;
  } catch (error) {
    console.error('updateUserPageSetting error:', error);
    throw error;
  }
};

/**
 * Updates failed call status.
 */
export const updateFailedCallStatus = async (uniqueId, mode) => {
  const { userId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        UniqueId: uniqueId,
        Mode: mode,
        UserId: userId
      })
    };
    const response = await serviceInstance.post('LeadAssignment/UpdateFailedCallStatus', payload);
    return response.data;
  } catch (error) {
    console.error('updateFailedCallStatus error:', error);
    throw error;
  }
};

/**
 * Reports call ended time.
 */
export const reportCallEndedTime = async (uniqueId) => {
  const { userId, parentId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        EndpointUniqueId: uniqueId,
        UserId: userId,
        ParentId: parentId
      })
    };
    const response = await serviceInstance.post('LeadAssignment/ReportCallEndedTime', payload);
    return response.data;
  } catch (error) {
    console.error('reportCallEndedTime error:', error);
    throw error;
  }
};

/**
 * Fetches call log master data (outcomes) from WCF.
 */
export const getCallLogMaster = async () => {
  const { userId } = getSession();
  try {
    const payload = { UserId: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CallReportService.asmx/GetCallLogMaster', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getCallLogMaster error:', error);
    throw error;
  }
};

/**
 * Fetches custom fields from WCF.
 */
export const getCustomFields = async () => {
  const { userId } = getSession();
  try {
    const payload = { UserId: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/GetCustomFieldByUserId', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getCustomFields error:', error);
    throw error;
  }
};

/**
 * Fetches user dialer settings (SELECT mode).
 */
export const getUserDialerSettings = async () => {

  const { userId, parentId, TokenId } = getSession();

  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        Mode: 'SELECT',
        Id: 0,
        UserId: userId
      })
    };
    const response = await serviceInstance.post('DialerSetting/GetScheduleCallAction', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getUserDialerSettings error:', error);
    throw error;
  }
};

/**
 * Fetches entity mobile numbers for schedule.
 */
export const getEntityMobileNos = async (entityName, entityId) => {
  const { userId } = getSession();
  try {
    const payload = {
      EntityName: entityName,
      EntityId: entityId,
      UserId: userId
    };
    const response = await serviceInstance.post('DialerSetting/GetEntityMobileNosForSchedule', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getEntityMobileNos error:', error);
    throw error;
  }
};

/**
 * Fetches hierarchy users for assignment via WCF.
 */
export const getHierarchyUsers = async () => {
  const { userId } = getSession();
  try {
    const payload = { userid: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/ToDoService.asmx/GetUserFromHashed_List', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getHierarchyUsers error:', error);
    throw error;
  }
};

/**
 * Fetches email accounts from WCF.
 */
export const getEmailAccounts = async () => {
  const { parentId } = getSession();
  try {
    const payload = {
      pUserId: parentId,
      isSMS: false
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getSmsAccount', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getEmailAccounts error:', error);
    throw error;
  }
};

/**
 * Fetches email templates from WCF.
 */
export const getEmailTemplates = async () => {
  const { parentId } = getSession();
  try {
    const payload = { pUserId: parentId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getEmailTemplateList', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getEmailTemplates error:', error);
    throw error;
  }
};

/**
 * Fetches "From" mail IDs.
 */
export const getFromMailIds = async () => {
  const { userId, parentId } = getSession();
  try {
    const payload = { strUserID: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/funcToGetFromMailID', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getFromMailIds error:', error);
    throw error;
  }
};

/**
 * Fetches SMS accounts from WCF.
 */
export const getSmsAccounts = async () => {
  const { parentId } = getSession();
  try {
    const payload = {
      pUserId: parentId,
      isSMS: true
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getSmsAccount', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getSmsAccounts error:', error);
    throw error;
  }
};

/**
 * Fetches SMS sender IDs from WCF.
 */
export const getSmsSenderIds = async () => {
  const { userId } = getSession();
  try {
    const payload = { UserId: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getSmsSenderIds', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getSmsSenderIds error:', error);
    throw error;
  }
};

/**
 * Fetches SMS templates by sender name from WCF.
 */
export const getSmsTemplates = async (senderName) => {
  const { parentId } = getSession();
  try {
    const payload = {
      SenderName: senderName,
      UserId: parentId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getSmsTemplates', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getSmsTemplates error:', error);
    throw error;
  }
};

/**
 * Fetches entity placeholders from WCF.
 */
export const getEntityPlaceholders = async () => {
  const { parentId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId || '',
      Details: {
        strEntityName: 'lead',
        strParentID: parentId
      }
    };
    const response = await serviceInstance.post('UserCRM/FuncToGetEntityPlaceHolder', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getEntityPlaceholders error:', error);
    throw error;
  }
};

/**
 * Fetches DID numbers from WCF.
 */
export const getDIDNumbers = async () => {
  const { parentId } = getSession();
  try {
    const payload = { pUserId: parentId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/GetDIDNo', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getDIDNumbers error:', error);
    throw error;
  }
};

/**
 * Fetches app names/flows from WCF.
 */
export const getAppNames = async () => {
  const { parentId } = getSession();
  try {
    const payload = { pUserId: parentId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getAppName', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getAppNames error:', error);
    throw error;
  }
};

/**
 * Fetches DLT template text by ID via WCF.
 */
export const getDLTTemplateText = async (dltTemplateId) => {
  const { userId } = getSession();
  try {
    const payload = {
      DLTTemplateID: dltTemplateId,
      UserId: userId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/Get_DLTTemplateText', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getDLTTemplateText error:', error);
    throw error;
  }
};

/**
 * Validates DLT template text via WCF.
 */
export const validateDLTTemplateText = async (dltTemplateId, message) => {
  try {
    const payload = {
      strDLTTemplateID: dltTemplateId,
      strMessage: message
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/ValidateDLTTemplateText', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('validateDLTTemplateText error:', error);
    throw error;
  }
};

/**
 * Sends SMS (Standard) via WCF.
 */
export const sendSms = async (smsData) => {
  const { userId, parentId } = getSession();
  try {
    const payload = {
      objSendSms_BO: smsData,
      ParentId: parentId,
      UserId: userId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/SendSms', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('sendSms error:', error);
    throw error;
  }
};

/**
 * Sends International SMS via Web API.
 */
export const sendInternationalSms = async (smsData) => {
  const { userId, TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      LoggedUserId: userId,
      Message: "",
      MAC_Address: "",
      IP_Address: "",
      Details: JSON.stringify({ objSendSms_BO: smsData })
    };
    const response = await serviceInstance.post('UserCRM/AddInternationalSmsSummary', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('sendInternationalSms error:', error);
    throw error;
  }
};

/**
 * Sends Email via WCF.
 */
export const sendEmail = async (emailData) => {
  const { userId } = getSession();
  try {
    const payload = {
      objSendEmail_BO: emailData,
      UserId: userId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/SendEmail', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('sendEmail error:', error);
    throw error;
  }
};

/**
 * Sends Voice Enquiry via WCF.
 */
export const sendVoice = async (voiceData) => {
  const { parentId } = getSession();
  try {
    const payload = {
      objSendVoice_BO: voiceData,
      ParentId: parentId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/SendVoiceEnquiry', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('sendVoice error:', error);
    throw error;
  }
};

/**
 * Adds Lead Followup via WCF.
 */
export const addFollowup = async (followupData, dltTemplateId) => {
  const { userId, parentId } = getSession();
  try {
    const payload = {
      objLeadFollowup_BO: followupData,
      strDLTTemplateID: dltTemplateId,
      strParentID: parentId,
      strUserID: userId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/AddLeadFollowup_DLT', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('addFollowup error:', error);
    throw error;
  }
};

/**
 * Adds Notes via WCF.
 */
export const addNotes = async (leadId, notes) => {
  const { userId, parentId } = getSession();
  try {
    const payload = {
      LeadID: leadId,
      Notes: notes,
      ParentId: parentId,
      UserId: userId
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/AddNotes', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('addNotes error:', error);
    throw error;
  }
};

/**
 * Inserts a new Task or Appointment via Web API (Latest).
 */
export const insertActivityLatest = async (taskData) => {
  const { TokenId } = getSession();
  try {
    const payload = {
      Token: TokenId,
      Details: JSON.stringify({
        insertUpdateTask: taskData,
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "India Standard Time"
      })
    };
    const response = await serviceInstance.post('UserCRM/InsertNewTaskOrAppointment_latest', payload);
    return response.data;
  } catch (error) {
    console.error('insertActivityLatest error:', error);
    throw error;
  }
};

/**
 * Inserts a new Task or Appointment via WCF MicrositeService.
 */
export const insertActivity = async (taskData) => {
  const { userId, parentId } = getSession();
  try {
    const payload = {
      insertUpdateTask: taskData,
      strUserID: String(userId),
      strParentID: String(parentId),
      strTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "India Standard Time"
    };
    const response = await wcfInstance.post('UserCRMCampaign/Service/MicrositeService.asmx/InsertNewTaskOrAppointment', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('insertActivity error:', error);
    throw error;
  }
};

/**
 * Fetches Deal Pipelines from WCF.
 */
export const getDealPipelines = async () => {
  const { parentId } = getSession();
  try {
    const payload = { pUserId: parentId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getDealPipeline', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getDealPipelines error:', error);
    throw error;
  }
};

/**
 * Fetches Deal Pipeline Stages from WCF.
 */
export const getDealPipelineStages = async (pipelineId) => {
  try {
    const payload = { pipelineId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getDealPipelineStage', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getDealPipelineStages error:', error);
    throw error;
  }
};

/**
 * Fetches Deal Pipeline Stage Reasons from WCF.
 */
export const getDealPipelineStageReason = async (pipelineId) => {
  try {
    const payload = { pipelineId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/getDealPipelineStageReason', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getDealPipelineStageReason error:', error);
    throw error;
  }
};

/**
 * Saves Deal Data via WCF.
 */
export const saveDealData = async (dealData) => {
  try {
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/SaveDealData', dealData);
    return response.data.d || response.data;
  } catch (error) {
    console.error('saveDealData error:', error);
    throw error;
  }
};

/**
 * Fetches Lead details by ID from WCF.
 */
export const getLeadById = async (leadId) => {
  try {
    const payload = { LeadId: leadId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/IMSService.asmx/GetLeadById', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getLeadById error:', error);
    throw error;
  }
};

/**
 * Fetches the subject of an email template via WCF.
 */
export const getTemplateSubject = async (templateId) => {
  try {
    const payload = { strTemplateID: templateId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/TriggerCampaign.asmx/FuncToGetTemplateSubject', payload);
    // This one usually returns raw string in .d
    return response.data.d || response.data;
  } catch (error) {
    console.error('getTemplateSubject error:', error);
    throw error;
  }
};

/**
 * Fetches web forms for the user via WCF.
 */
export const getWebForms = async () => {
  const { userId } = getSession();
  try {
    const payload = { UserId: userId };
    const response = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/GetUserWebformListByUserId', payload);
    return parseDetails(response.data);
  } catch (error) {
    console.error('getWebForms error:', error);
    throw error;
  }
};

const dialerService = {
  updateDialerData,
  getScheduleCallForDialer,
  getScheduleCallDataForGrid,
  updateUserPageSetting,
  getCallLogMaster,
  getCustomFields,
  getUserDialerSettings,
  getEntityMobileNos,
  updateFailedCallStatus,
  reportCallEndedTime,
  getHierarchyUsers,
  getEmailAccounts,
  getEmailTemplates,
  getFromMailIds,
  getSmsAccounts,
  getSmsSenderIds,
  getSmsTemplates,
  getWebForms,
  getEntityPlaceholders,
  getDIDNumbers,
  getAppNames,
  getDLTTemplateText,
  validateDLTTemplateText,
  sendSms,
  sendInternationalSms,
  sendEmail,
  sendVoice,
  addFollowup,
  addNotes,
  insertActivity,
  getAppNames,
  getDealPipelines,
  getDealPipelineStages,
  getDealPipelineStageReason,
  getTemplateSubject,
  getLeadById,
  saveDealData,
  insertActivityLatest
};

export default dialerService;

